'use client';

import { useState, Fragment } from 'react';
import Link from 'next/link';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, ChevronDown, ArrowRight, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { pricingData } from '@/components/marketing-tivion/page-data/pricing';

/* ─── Billing Toggle ─── */

function BillingToggle({ interval, onChange }) {
  return (
    <div className="flex flex-col items-center gap-3">
      <div className="relative inline-flex items-center rounded-full border border-border bg-card p-1">
        {/* Sliding indicator */}
        <div
          className={`absolute top-1 bottom-1 w-[calc(50%-4px)] rounded-full bg-shield-cyan/15 border border-shield-cyan/30 transition-transform duration-300 ease-out ${
            interval === 'yearly' ? 'translate-x-[calc(100%+4px)]' : 'translate-x-0'
          }`}
        />
        <button
          type="button"
          onClick={() => onChange('monthly')}
          className={`relative z-10 px-5 py-2 text-sm font-medium rounded-full transition-colors ${
            interval === 'monthly' ? 'text-foreground' : 'text-muted-foreground'
          }`}
        >
          Monthly
        </button>
        <button
          type="button"
          onClick={() => onChange('yearly')}
          className={`relative z-10 px-5 py-2 text-sm font-medium rounded-full transition-colors ${
            interval === 'yearly' ? 'text-foreground' : 'text-muted-foreground'
          }`}
        >
          Yearly
        </button>
      </div>

      <span
        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-shield-cyan/10 text-shield-cyan text-xs font-medium border border-shield-cyan/20"
      >
        <Sparkles className="h-3 w-3" />
        Save up to 20%
      </span>
    </div>
  );
}

/* ─── Animated Price ─── */

function AnimatedPrice({ price, interval }) {
  return (
    <div className="flex items-baseline gap-1">
      <AnimatePresence mode="wait">
        <motion.span
          key={`${price}-${interval}`}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -12 }}
          transition={{ duration: 0.2 }}
          className="text-5xl font-bold tracking-tight font-mono"
        >
          ${price}
        </motion.span>
      </AnimatePresence>
      {price > 0 && (
        <span className="text-lg text-muted-foreground font-normal">/mo</span>
      )}
    </div>
  );
}

/* ─── Plan Card ─── */

function PlanCard({ tier, interval }) {
  const price = tier.prices[interval];
  const monthlyPrice = tier.prices.monthly;
  const isYearly = interval === 'yearly';
  const showSavings = isYearly && price > 0 && price < monthlyPrice;

  const borderClass =
    tier.borderStyle === 'cyan'
      ? 'border-shield-cyan/40 shadow-[0_0_30px_rgba(79,209,229,0.12)]'
      : tier.borderStyle === 'violet'
        ? 'border-violet-500/30'
        : 'border-border';

  return (
    <div
      className={`relative flex flex-col rounded-2xl border bg-card p-8 transition-all duration-300 hover:border-shield-cyan/20 ${borderClass} ${
        tier.featured ? 'lg:scale-[1.03] z-10' : ''
      }`}
    >
      {/* Most Popular badge */}
      {tier.featured && (
        <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
          <span className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-shield-cyan text-[11px] font-semibold uppercase tracking-wider text-primary-foreground">
            Most Popular
          </span>
        </div>
      )}

      {/* Plan name + ideal for */}
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-foreground">{tier.marketingName}</h3>
        <p className="text-sm text-muted-foreground mt-1">{tier.idealFor}</p>
      </div>

      {/* Price */}
      <div className="mb-2">
        {price === 0 ? (
          <span className="text-5xl font-bold tracking-tight">Free</span>
        ) : (
          <AnimatedPrice price={price} interval={interval} />
        )}
      </div>

      {/* Billing note */}
      <div className="h-5 mb-6">
        {showSavings && (
          <p className="text-xs text-muted-foreground">
            <span className="line-through">${monthlyPrice}/mo</span>
            <span className="text-shield-cyan ml-1.5">billed annually</span>
          </p>
        )}
        {price === 0 && (
          <p className="text-xs text-muted-foreground">No credit card required</p>
        )}
      </div>

      {/* CTA */}
      <Button
        asChild
        size="lg"
        className={`w-full mb-8 font-medium ${
          tier.featured
            ? 'bg-shield-cyan text-primary-foreground hover:bg-shield-cyan/90'
            : tier.borderStyle === 'violet'
              ? 'bg-violet-600 text-white hover:bg-violet-600/90'
              : 'bg-card text-foreground border border-border hover:bg-muted/50'
        }`}
      >
        <Link href={tier.cta.href}>{tier.cta.label}</Link>
      </Button>

      {/* Feature list */}
      <ul className="space-y-3 flex-1">
        {tier.featureHighlights.map((feature) => (
          <li key={feature} className="flex items-start gap-3 text-sm">
            <Check className="h-4 w-4 text-shield-cyan shrink-0 mt-0.5" />
            <span className="text-muted-foreground">{feature}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ─── Comparison Table Cell ─── */

function ComparisonCell({ value }) {
  if (value === true) {
    return <Check className="h-4 w-4 text-shield-cyan mx-auto" />;
  }
  if (value === false) {
    return <span className="text-muted-foreground/40">—</span>;
  }
  return <span className="text-sm text-foreground font-mono">{value}</span>;
}

/* ─── Comparison Table ─── */

function ComparisonTable({ interval }) {
  const [expanded, setExpanded] = useState(false);
  const { comparisonTable, tiers } = pricingData;

  // Show first 2 categories collapsed, all when expanded
  const visibleCategories = expanded ? comparisonTable : comparisonTable.slice(0, 2);

  return (
    <section className="py-16 lg:py-24">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-foreground mb-3">
            Compare plans in detail
          </h2>
          <p className="text-muted-foreground">
            Find the right level of market intelligence for your team.
          </p>
        </div>

        {/* Table wrapper — scrollable on mobile */}
        <div className="overflow-x-auto -mx-6 px-6">
          <table className="w-full min-w-[640px]">
            {/* Header */}
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-4 pr-4 text-sm font-medium text-muted-foreground w-[200px] lg:w-[260px] sticky left-0 bg-background z-10" />
                {tiers.map((tier) => (
                  <th
                    key={tier.id}
                    className={`text-center py-4 px-3 text-sm font-semibold ${
                      tier.featured ? 'text-shield-cyan' : 'text-foreground'
                    }`}
                  >
                    <div>{tier.marketingName}</div>
                    <div className="text-xs font-normal text-muted-foreground mt-0.5">
                      {tier.prices[interval] === 0
                        ? 'Free'
                        : `$${tier.prices[interval]}/mo`}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {visibleCategories.map((category) => (
                <Fragment key={category.category}>
                  {/* Category header */}
                  <tr>
                    <td
                      colSpan={5}
                      className="pt-8 pb-3 text-xs font-semibold uppercase tracking-wider text-shield-cyan sticky left-0 bg-background z-10"
                    >
                      {category.category}
                    </td>
                  </tr>

                  {/* Rows */}
                  {category.rows.map((row) => (
                    <tr key={row.label} className="border-b border-border/30 hover:bg-muted/5 transition-colors">
                      <td className="py-3 pr-4 text-sm text-muted-foreground sticky left-0 bg-background z-10">
                        {row.label}
                      </td>
                      {row.values.map((value, i) => (
                        <td key={i} className="py-3 px-3 text-center">
                          <ComparisonCell value={value} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>

        {/* Expand toggle */}
        {!expanded && (
          <div className="text-center mt-8">
            <button
              type="button"
              onClick={() => setExpanded(true)}
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full border border-border bg-card text-sm font-medium text-muted-foreground hover:text-foreground hover:border-shield-cyan/20 transition-colors"
            >
              Show full comparison
              <ChevronDown className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

/* ─── FAQ Section ─── */

function FAQ() {
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <section className="py-16 lg:py-24 bg-card/50">
      <div className="mx-auto max-w-3xl px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-foreground mb-3">
            Frequently asked questions
          </h2>
        </div>

        <div className="space-y-2">
          {pricingData.faqs.map((faq, index) => {
            const isOpen = openIndex === index;
            return (
              <div key={index} className="rounded-xl border border-border bg-card overflow-hidden">
                <button
                  type="button"
                  onClick={() => setOpenIndex(isOpen ? null : index)}
                  className="flex items-center justify-between w-full px-6 py-4 text-left text-sm font-medium text-foreground hover:text-shield-cyan transition-colors"
                >
                  {faq.question}
                  <ChevronDown
                    className={`h-4 w-4 text-muted-foreground shrink-0 ml-4 transition-transform duration-200 ${
                      isOpen ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                <AnimatePresence>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-4 text-sm text-muted-foreground leading-relaxed">
                        {faq.answer}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ─── Final CTA ─── */

function PricingCTA() {
  return (
    <section className="py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="relative rounded-2xl bg-gradient-to-b from-card to-background border border-border overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-96 bg-shield-cyan/5 rounded-full blur-3xl" />
          </div>

          <div className="relative px-8 py-20 sm:px-16 sm:py-24 text-center">
            <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-foreground mb-4 text-balance">
              Ready to see the market clearly?
            </h2>
            <p className="text-lg text-muted-foreground max-w-xl mx-auto mb-10">
              Start with a free plan. Upgrade when you need deeper intelligence.
            </p>

            <Button
              size="lg"
              className="bg-shield-cyan text-primary-foreground hover:bg-shield-cyan/90 font-medium px-10 py-6 text-lg"
              asChild
            >
              <Link href="/onboarding/identity">Get Started Free</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Main Pricing Page ─── */

export function PricingPage() {
  const [interval, setInterval] = useState('monthly');
  const paidTiers = pricingData.tiers.filter((t) => t.id !== 'free');
  const freeTier = pricingData.tiers.find((t) => t.id === 'free');

  return (
    <>
      {/* Hero */}
      <section className="relative pt-32 pb-4 lg:pt-40 lg:pb-8 overflow-hidden">
        {/* Background glow behind center (most popular) card area */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/3 w-[600px] h-[600px] bg-shield-cyan/[0.04] rounded-full blur-3xl pointer-events-none" />

        <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-foreground mb-4 text-balance">
              Insights that drive your strategy.
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              Choose the right level of market intelligence. From competitive awareness to full-spectrum strategic monitoring.
            </p>

            <BillingToggle interval={interval} onChange={setInterval} />
          </div>
        </div>
      </section>

      {/* Plan Cards */}
      <section className="pb-16 lg:pb-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-6 lg:gap-4 items-start">
            {paidTiers.map((tier) => (
              <div
                key={tier.id}
                className={tier.featured ? 'order-first lg:order-none' : ''}
              >
                <PlanCard tier={tier} interval={interval} />
              </div>
            ))}
          </div>

          {/* Free plan callout */}
          {freeTier && (
            <div className="mt-8 text-center">
              <p className="text-sm text-muted-foreground">
                Just getting started?{' '}
                <Link
                  href={freeTier.cta.href}
                  className="inline-flex items-center gap-1 text-shield-cyan font-medium hover:underline"
                >
                  Try our Free plan
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
                {' '}&mdash; 10 keywords, 1 domain, weekly SERP checks.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Feature Comparison */}
      <ComparisonTable interval={interval} />

      {/* FAQ */}
      <FAQ />

      {/* Final CTA */}
      <PricingCTA />
    </>
  );
}
